import * as React from "react";
import {observer} from "mobx-react";
import type {Lambda} from "mobx";
import {observe, reaction} from "mobx";
import {withAppStores} from "../../../AppStores";
import {SidePanel} from "../abstract/sidepanel/SidePanel";
import {CatalogItemPanel} from "../catalog/create/CatalogItemPanel";
import {EditViewSharing} from "../abstract/view/sharing/EditViewSharing";
import type {IIconConfig} from "../catalog/create/CatalogTypes";
import {XyiconFeature} from "../../../generated/api/base";
import type {AppState} from "../../../data/state/AppState";
import {Splitter} from "../../widgets/splitter/Splitter";
import type {TransportLayer} from "../../../data/TransportLayer";
import type {ISpaceItemModel, IModel} from "../../../data/models/Model";
import type {IFilterState} from "../../../data/models/filter/Filter";
import type {Space} from "../../../data/models/Space";
import type {Boundary} from "../../../data/models/Boundary";
import type {Xyicon} from "../../../data/models/Xyicon";
import type {SpaceFileInsertionInfo, RescaleSpaceRequest, SpaceFileDto, UpdateSpaceFileRequest} from "../../../generated/api/base";
import {XHRLoader} from "../../../utils/loader/XHRLoader";
import {Functions} from "../../../utils/function/Functions";
import type {Catalog} from "../../../data/models/Catalog";
import {ReactUtils} from "../../utils/ReactUtils";
import {ObjectUtils} from "../../../utils/data/ObjectUtils";
import {ManageColumnsPanel} from "../abstract/columns/ManageColumnsPanel";
import type {ICaptionSettings} from "../../../data/models/ViewUtils";
import {SpaceToPDFExportPanel} from "./spaceeditor/ui/viewbar/SpaceToPDFExportPanel";
import {SpaceEditorMode} from "./spaceeditor/logic3d/renderers/SpaceViewRendererUtils";
import {arrayOfSelectionToolTypes} from "./spaceeditor/logic3d/features/tools/Tools";
import type {SelectionToolType, SpaceTool} from "./spaceeditor/logic3d/features/tools/Tools";
import type {OverlayPanel} from "./SpaceView";
import {CaptionPanel} from "./spaceeditor/ui/viewbar/CaptionPanel";
import {ConditionalFormattingPanel} from "./spaceeditor/ui/viewbar/ConditionalFormattingPanel";
import {LayerPanel} from "./spaceeditor/ui/viewbar/LayerPanel";
import {Dockable} from "./spaceeditor/ui/toolbar/Dockable";
import type {DockableTitle} from "./spaceeditor/ui/toolbar/Dockable";
import {SpaceEditor} from "./spaceeditor/ui/SpaceEditor";
import type {SpaceViewRenderer} from "./spaceeditor/logic3d/renderers/SpaceViewRenderer";

interface IBasicPropsFor3DTweaking {
	readonly spaceViewRenderer: SpaceViewRenderer;
	readonly appState?: AppState;
	readonly transport?: TransportLayer;
}

interface ISpaceViewState {
	selectedItems: ISpaceItemModel[];
	isCatalogOpen: boolean;
	isCatalogDocked: boolean;
	isBoundaryTypesWindowOpen: boolean;
	isBoundaryTypesWindowDocked: boolean;
	isUnplottedXyiconsOpen: boolean;
	isUnplottedXyiconsDocked: boolean;
	space: Space;
	activeToolId: SpaceTool;
	selectionToolType: SelectionToolType;
	isLayerPanelOpen: boolean;
	isConditionalFormattingPanelOpen: boolean;
	isCaptionPanelOpen: boolean;
	isCreatePanelOpen: boolean;
	editedCatalog: {
		config: IIconConfig;
		catalog: Catalog;
	};
	catalogMode: "create" | "edit";
	managingColumns: boolean;
	sharingViewId: string;
	isSpaceToPDFExportPanelOpen: boolean;
}

@withAppStores("appState", "transport", "app")
@observer
export class SpaceEditorView extends React.Component<IBasicPropsFor3DTweaking, ISpaceViewState> {
	private _appState: AppState;

	private readonly _feature = XyiconFeature.SpaceEditor;
	private _requestAnimationFrameId: number = null;
	private readonly _sidePanel = React.createRef<SidePanel<IModel>>();
	private readonly _layerPanel = React.createRef<LayerPanel>();
	private readonly _conditionalFormattingPanel = React.createRef<ConditionalFormattingPanel>();
	private readonly _captionPanel = React.createRef<CaptionPanel>();
	private _filteredItems: ISpaceItemModel[] = [];
	private _splitter: Splitter;

	private _disposerForViewChangeListener: Lambda;
	private _disposerForFilterListener: Lambda;

	private _lastViewId: string = "";
	private _timeoutIds: Record<string, number> = {};

	private readonly _updateFilterStatePromiseResolveSet: Set<(value: void | PromiseLike<void>) => void> = new Set();

	constructor(props: IBasicPropsFor3DTweaking) {
		super(props);
		this._appState = props.appState;

		this.state = {
			selectedItems: [],
			isCatalogOpen: false,
			isCatalogDocked: false,
			isBoundaryTypesWindowOpen: false,
			isBoundaryTypesWindowDocked: false,
			isUnplottedXyiconsOpen: false,
			isUnplottedXyiconsDocked: false,
			space: null,
			activeToolId: "selection",
			selectionToolType: "selection",
			isLayerPanelOpen: false,
			isConditionalFormattingPanelOpen: false,
			isCaptionPanelOpen: false,
			isCreatePanelOpen: false,
			editedCatalog: null,
			catalogMode: "create",
			managingColumns: false,
			sharingViewId: null,
			isSpaceToPDFExportPanelOpen: false,
		};
	}

	private resetFilters() {
		const selectedView = this.selectedView;

		if (selectedView.id !== this._lastViewId) {
			let prevBoundaryCaptions: ICaptionSettings | null = null;
			let prevXyiconCaptions: ICaptionSettings | null = null;

			if (this._lastViewId) {
				const prevView = this.props.spaceViewRenderer.actions.getViewById(this._lastViewId);
				if (prevView) {
					const prevCaptions = prevView.spaceEditorViewSettings.captions;
					prevBoundaryCaptions = {...prevCaptions.boundary};
					prevXyiconCaptions = {...prevCaptions.xyicon};
				}
			}
			// Selected view changed, or opening view the first time
			// -> copy filters from view
			this._lastViewId = selectedView.id;

			const newCaptions = selectedView.spaceEditorViewSettings.captions;
			const newBoundaryCaptions = {...newCaptions.boundary};
			const newXyiconCaptions = {...newCaptions.xyicon};

			const needFullCaptionRefreshBecauseBoundaryCaptionsChanged = JSON.stringify(prevBoundaryCaptions) !== JSON.stringify(newBoundaryCaptions);
			const needFullCaptionRefreshBecauseXyiconCaptionsChanged = JSON.stringify(prevXyiconCaptions) !== JSON.stringify(newXyiconCaptions);
			const needFullCaptionUpdate = needFullCaptionRefreshBecauseBoundaryCaptionsChanged || needFullCaptionRefreshBecauseXyiconCaptionsChanged;

			const savedFilters = selectedView.getSavedFilters();
			const clonedSavedFilters = ObjectUtils.deepClone(savedFilters);
			selectedView.setFilters(clonedSavedFilters);

			this.updateSpaceEditorFilterState(savedFilters, !needFullCaptionUpdate).then(() => {
				if (needFullCaptionUpdate) {
					let captionFeature = XyiconFeature.Xyicon;
					if (needFullCaptionRefreshBecauseBoundaryCaptionsChanged) {
						// boundary captions can affect xyicons, but not the other way around
						captionFeature = XyiconFeature.Boundary;
					}
					this.props.spaceViewRenderer.actions.updateSpaceEditorCaptions(captionFeature);
				}
			});
			this.listenFilters();
			this._sidePanel.current?.filterEditor?.reset(clonedSavedFilters);
		}
	}

	private onOpenSpaceToPDFExportPanel = () => {
		this.setState({
			isSpaceToPDFExportPanelOpen: true,
		});
	};

	private onCloseSpaceToPDFExportPanel = () => {
		this.setState({
			isSpaceToPDFExportPanelOpen: false,
		});
	};

	private setDockableDocked = (value: boolean, title: DockableTitle) => {
		if (title === "Catalog") {
			this.setState({
				isCatalogDocked: value,
			});

			if (value === this.state.isBoundaryTypesWindowDocked) {
				this.setState({
					isBoundaryTypesWindowOpen: false,
				});
			} else if (value === this.state.isUnplottedXyiconsDocked) {
				this.setState({
					isUnplottedXyiconsOpen: false,
				});
			}
		} else if (title === "Boundary") {
			this.setState({
				isBoundaryTypesWindowDocked: value,
			});

			if (value === this.state.isCatalogDocked) {
				this.setState({
					isCatalogOpen: false,
				});
			} else if (value === this.state.isUnplottedXyiconsDocked) {
				this.setState({
					isUnplottedXyiconsOpen: false,
				});
			}
		} else if (title === "Unplotted Xyicons") {
			this.setState({
				isUnplottedXyiconsDocked: value,
			});

			if (value === this.state.isCatalogDocked) {
				this.setState({
					isCatalogOpen: false,
				});
			}
			if (value === this.state.isBoundaryTypesWindowDocked) {
				this.setState({
					isBoundaryTypesWindowOpen: false,
				});
			}
		}
	};

	private isDocked(title: DockableTitle) {
		if (title === "Catalog") {
			return this.state.isCatalogDocked;
		} else if (title === "Boundary") {
			return this.state.isBoundaryTypesWindowDocked;
		} else if (title === "Unplotted Xyicons") {
			return this.state.isUnplottedXyiconsDocked;
		}
	}

	private setDockableOpen = (value: boolean, title: DockableTitle, onlyIfNotDocked: boolean = false) => {
		const isDocked = this.isDocked(title);

		if ((onlyIfNotDocked && !isDocked) || !onlyIfNotDocked) {
			if (title === "Catalog") {
				this.setState({
					isCatalogOpen: value,
				});

				if (value && this.state.isBoundaryTypesWindowOpen && this.state.isBoundaryTypesWindowDocked === this.state.isCatalogDocked) {
					this.setState({
						isBoundaryTypesWindowOpen: false,
					});
				}
				if (value && this.state.isUnplottedXyiconsOpen && this.state.isUnplottedXyiconsDocked === this.state.isCatalogDocked) {
					this.setState({
						isUnplottedXyiconsOpen: false,
					});
				}
			} else if (title === "Boundary") {
				this.setState({
					isBoundaryTypesWindowOpen: value,
				});

				if (value && this.state.isCatalogOpen && this.state.isCatalogDocked === this.state.isBoundaryTypesWindowDocked) {
					this.setState({
						isCatalogOpen: false,
					});
				}
				if (value && this.state.isUnplottedXyiconsOpen && this.state.isUnplottedXyiconsDocked === this.state.isBoundaryTypesWindowDocked) {
					this.setState({
						isUnplottedXyiconsOpen: false,
					});
				}
			} else if (title === "Unplotted Xyicons") {
				this.setState({
					isUnplottedXyiconsOpen: value,
				});

				if (value && this.state.isCatalogOpen && this.state.isCatalogDocked === this.state.isUnplottedXyiconsDocked) {
					this.setState({
						isCatalogOpen: false,
					});
				}
				if (value && this.state.isBoundaryTypesWindowOpen && this.state.isBoundaryTypesWindowDocked === this.state.isUnplottedXyiconsDocked) {
					this.setState({
						isBoundaryTypesWindowOpen: false,
					});
				}
			}
		}
	};

	private selectItems = (spaceItems: ISpaceItemModel[], selectDetailsContent?: boolean, forceUpdate: boolean = false) => {
		if (forceUpdate || !ObjectUtils.compare(this.state.selectedItems, spaceItems)) {
			this.setState({
				selectedItems: spaceItems,
			});

			if (spaceItems.length > 0) {
				if (selectDetailsContent) {
					this.selectTab("details");
					this.closeOverlayPanel("Layer");
					this.closeOverlayPanel("ConditionalFormatting");
					this.closeOverlayPanel("Caption");
				}
			}
		}
	};

	private setScale = async (spaceUnitsPerMeter: number) => {
		const {space} = this.props.appState;
		const params: RescaleSpaceRequest = {
			spaceID: space.id,
			unitsPerMeter: spaceUnitsPerMeter,
			portfolioID: this.props.appState.activePortfolioId,
		};

		await this.props.transport.requestForOrganization<SpaceFileDto>({
			url: "spaces/rescale",
			method: XHRLoader.METHOD_POST,
			params: params,
		});

		space.setSpaceUnitsPerMeter(spaceUnitsPerMeter);

		await this.props.spaceViewRenderer.refreshSpace(true);
	};

	private confirmAlignment = async (insertionInfo: SpaceFileInsertionInfo) => {
		const activePDFFile = this.props.spaceViewRenderer.space.selectedSpaceFile;

		activePDFFile.setInsertionInfo(insertionInfo);

		const params: UpdateSpaceFileRequest = {
			spaceFileList: [
				{
					spaceFileID: activePDFFile.id,
					spaceVersionID: activePDFFile.spaceVersionId,
					spaceID: activePDFFile.parent.id,
					settings: {
						insertionInfo: activePDFFile.insertionInfo,
					},
				},
			],
			portfolioID: activePDFFile.parent.portfolioId,
		};

		await this.props.transport.updateSpaceFiles(params);
		this.props.spaceViewRenderer.setInsertionInfo(activePDFFile.insertionInfo);

		this.forceUpdate();
	};

	private async loadSpace(space: Space) {
		await this.props.spaceViewRenderer.populateData(space);
		this.setState({
			space: space,
		});
	}

	private updateSpace = async () => {
		const space = this.props.appState.space;

		if (space) {
			if (this.props.spaceViewRenderer.space?.id !== space.id) {
				await this.loadSpace(space);
			}
		}
	};

	private setActiveTool = (id: SpaceTool) => {
		const {spaceViewRenderer} = this.props;

		if (arrayOfSelectionToolTypes.includes(id as SelectionToolType)) {
			this.setSelectionToolType(id as SelectionToolType);
		}

		if (this.state.activeToolId !== id) {
			this.setState({
				activeToolId: id,
			});
		}

		if (id !== "selection") {
			spaceViewRenderer.spaceItemController.closeLinks();
			spaceViewRenderer.spaceItemController.deselectAll();
		}
	};

	private setSelectionToolType = (type: SelectionToolType) => {
		if (type !== this.state.selectionToolType) {
			this.setState({
				selectionToolType: type,
			});
		}
	};

	private getDockedTitle(): DockableTitle {
		if (this.state.isCatalogOpen && this.state.isCatalogDocked) {
			return "Catalog";
		} else if (this.state.isBoundaryTypesWindowOpen && this.state.isBoundaryTypesWindowDocked) {
			return "Boundary";
		} else if (this.state.isUnplottedXyiconsOpen && this.state.isUnplottedXyiconsDocked) {
			return "Unplotted Xyicons";
		} else {
			return null;
		}
	}

	private getFilteredItems() {
		return this.props.spaceViewRenderer.spaceItemController.getFilteredItems();
	}

	public selectTab = (id: string) => {
		this._sidePanel.current?.selectTab(id);
	};

	private onClickOnItem = (item: Boundary | Xyicon) => {
		if (this.props.spaceViewRenderer.isMounted) {
			this.props.spaceViewRenderer.actions.navigateToSpaceItem(item, false);
		}
	};

	private onAddCatalogClick = () => {
		this.setState({
			isCreatePanelOpen: true,
			editedCatalog: null,
			catalogMode: "create",
		});
	};

	private onDuplicateCatalogClick = async (catalog: Catalog) => {
		const iconConfig = await this.props.transport.getIconConfigOfCatalog(catalog);

		this.setState({
			isCreatePanelOpen: true,
			editedCatalog: {
				catalog: catalog,
				config: iconConfig,
			},
			catalogMode: "create",
		});
	};

	private closeCreatePanel = () => {
		this.setState({
			isCreatePanelOpen: false,
			editedCatalog: null,
		});
	};

	private forceUpdateArrow = () => {
		this.forceUpdate();
	};

	private getOverlayPanelRef(type: OverlayPanel) {
		switch (type) {
			case "Layer":
				return this._layerPanel;
			case "ConditionalFormatting":
				return this._conditionalFormattingPanel;
			case "Caption":
				return this._captionPanel;
		}
	}

	private closeOverlayPanel(type: OverlayPanel) {
		const stateName: keyof ISpaceViewState = `is${type}PanelOpen`;
		const ref = this.getOverlayPanelRef(type).current as any;

		if (ref?.state?.isOpen) {
			ref?.setState({
				isOpen: false,
			});
		}

		clearTimeout(this._timeoutIds[stateName]);

		this._timeoutIds[stateName] = window.setTimeout(() => {
			if (this.state[stateName]) {
				this.setState({
					[stateName]: false,
				} as any);
			}
		}, 200); // derivated from CSS (0.2s)
	}

	private onToggleOverlayPanel(type: OverlayPanel) {
		const stateName = `is${type}PanelOpen`;
		const otherStateNames = ["isLayerPanelOpen", "isConditionalFormattingPanelOpen", "isCaptionPanelOpen"].filter((value) => value !== stateName);
		const currentState = this.state[stateName as keyof ISpaceViewState];

		if (!currentState) {
			this.setState({
				[stateName]: true,
			} as any);

			for (const otherStateName of otherStateNames) {
				this.setState({
					[otherStateName]: false,
				} as any);
			}

			const minWidth = 550; //px
			// Set the width of the splitter to ${minWidth}px, if it's smaller than that, and turning on captions, formatting, or layers
			// See #4102 for more details: https://dev.azure.com/xyicon/SpaceRunner%20V4/_workitems/edit/4102)

			const ratios = [...this._splitter.ratios];
			const numberOfSections = ratios.length;
			const ratioOfRightSection = ratios.pop();
			const ratioOfMiddleSection = ratios.pop();
			const browserWidth = window.innerWidth; // 1920; // We designed our webapplication with full hd screen size in mind, because most of our customers use this resolution
			const widthOfRightSection = ratioOfRightSection * browserWidth;
			const widthOfMiddleSection = ratioOfMiddleSection * browserWidth;

			if (widthOfRightSection < minWidth) {
				const difference = minWidth - widthOfRightSection;
				const ratioOfLeftSection = ratios.pop();

				if (widthOfMiddleSection < difference) {
					// can't set it to negative, so let's split it up to 2 equal parts
					if (numberOfSections === 2) {
						this._splitter.setRatios([0.5, 0.5]);
					} // if (numberOfSections === 3)
					else {
						const newRatioOfMiddleAndRight = (1 - ratioOfLeftSection) / 2;

						this._splitter.setRatios([ratioOfLeftSection, newRatioOfMiddleAndRight, newRatioOfMiddleAndRight]);
					}
				} else {
					const newRatioOfRightSection = minWidth / browserWidth;

					if (numberOfSections === 2) {
						this._splitter.setRatios([1 - newRatioOfRightSection, newRatioOfRightSection]);
					} // if (numberOfSections === 3)
					else {
						this._splitter.setRatios([ratioOfLeftSection, 1 - ratioOfLeftSection - newRatioOfRightSection, newRatioOfRightSection]);
					}
				}
			}
		} else {
			this.closeOverlayPanel(type);
		}
	}

	private spaceViewShareClick = (viewId: string) => {
		this.toggleSharingPanel(viewId);
	};

	private onToggleLayerPanel = () => {
		this.onToggleOverlayPanel("Layer");
	};

	private onToggleConditionalFormattingPanel = () => {
		this.onToggleOverlayPanel("ConditionalFormatting");
	};

	private onToggleCaptionPanel = () => {
		this.onToggleOverlayPanel("Caption");
	};

	private getOpenOverlayPanelType(): OverlayPanel {
		if (this.state.isLayerPanelOpen) {
			return "Layer";
		} else if (this.state.isConditionalFormattingPanelOpen) {
			return "ConditionalFormatting";
		} else if (this.state.isCaptionPanelOpen) {
			return "Caption";
		}

		return null;
	}

	private getOverlayPanel() {
		if (this.state.isLayerPanelOpen) {
			return (
				<LayerPanel
					ref={this._layerPanel}
					spaceViewRenderer={this.props.spaceViewRenderer}
					showNumbers={true}
					view={this.selectedView}
					onToggle={this.onToggleLayerPanel}
				/>
			);
		} else if (this.state.isConditionalFormattingPanelOpen) {
			return (
				<ConditionalFormattingPanel
					ref={this._conditionalFormattingPanel}
					spaceViewRenderer={this.props.spaceViewRenderer}
					view={this.selectedView}
					onToggle={this.onToggleConditionalFormattingPanel}
				/>
			);
		} else if (this.state.isCaptionPanelOpen) {
			return (
				<CaptionPanel
					ref={this._captionPanel}
					spaceViewRenderer={this.props.spaceViewRenderer}
					view={this.selectedView}
					onToggle={this.onToggleCaptionPanel}
				/>
			);
		} else {
			return null;
		}
	}

	private get selectedView() {
		return this._appState.actions.getSelectedView(XyiconFeature.SpaceEditor);
	}

	private onManageColumns = () => {
		this.setState({managingColumns: true});
	};

	private onCloseManageColumns = () => {
		this.setState({managingColumns: false});
	};

	private toggleSharingPanel = (viewId?: string) => {
		this.setState({sharingViewId: viewId});
	};

	private getSidePanel() {
		const features = [XyiconFeature.Xyicon, XyiconFeature.Boundary];

		this._filteredItems = this.getFilteredItems();

		return (
			<div className="relative">
				<SidePanel
					features={features}
					feature={XyiconFeature.SpaceEditor}
					items={this._filteredItems}
					selected={this.state.selectedItems}
					onSelect={Functions.emptyFunction}
					onClick={this.onClickOnItem}
					showGridForFeatures={[XyiconFeature.Xyicon, XyiconFeature.Boundary]}
					ref={this._sidePanel}
					isPortTemplateEditorOpen={false}
					setPortTemplateEditorOpen={Functions.emptyFunction}
					onManageColumns={this.onManageColumns}
					loading={!this.props.spaceViewRenderer.isMounted}
				/>
				{this.getOverlayPanel()}
			</div>
		);
	}

	private stringifyFilters = () => {
		return JSON.stringify(this.selectedView.filters);
	};

	private startListening() {
		this._disposerForViewChangeListener = observe(this.props.appState.selectedViewId, (this._feature as any).toString(), (a) => {
			this.resetFilters();
		});

		this.listenFilters();
	}

	private listenFilters() {
		this._disposerForFilterListener?.();
		this._disposerForFilterListener = reaction(this.stringifyFilters, () => {
			this.updateSpaceEditorFilterState(this.selectedView.filters);
		});
	}

	private stopListening() {
		this._disposerForViewChangeListener?.();
		this._disposerForViewChangeListener = null;

		this._disposerForFilterListener?.();
		this._disposerForFilterListener = null;
	}

	private onSplitterDidMount = (splitter: Splitter) => {
		if (splitter) {
			this._splitter = splitter;
			const ratios = this.props.transport.services.localStorage.getSplitterRatios();

			if (ratios && ratios.length >= 2) {
				splitter.setRatios(ratios);
				this.props.spaceViewRenderer.onResize();
			}
		}
	};

	private onSplitterChange = (ratios: number[]) => {
		const {transport} = this.props;

		transport.services.localStorage.setSplitterRatios(ratios);
	};

	private saveStateToLocalStorage() {
		const organizationId = this.props.appState.organizationIdByAuthData;

		if (organizationId) {
			const stateToSave = {
				isBoundaryTypesWindowDocked: this.state.isBoundaryTypesWindowDocked,
				isBoundaryTypesWindowOpen: this.state.isBoundaryTypesWindowOpen && this.state.isBoundaryTypesWindowDocked,
				isCatalogDocked: this.state.isCatalogDocked,
				isCatalogOpen: this.state.isCatalogOpen && this.state.isCatalogDocked,
				isUnplottedXyiconsDocked: this.state.isUnplottedXyiconsDocked,
				isUnplottedXyiconsOpen: this.state.isUnplottedXyiconsOpen && this.state.isUnplottedXyiconsDocked,
			};

			this.props.transport.services.localStorage.set(this.getLocalStorageKey(organizationId), stateToSave);
		}
	}

	private getLocalStorageKey(organizationId: string) {
		return `srv4-org-${organizationId}-spaceeditor-spaceview-dockables-open-state`;
	}

	private loadStateFromLocalStorage() {
		const organizationId = this.props.appState.organizationIdByAuthData;

		if (organizationId) {
			const savedDockedState = this.props.transport.services.localStorage.get(this.getLocalStorageKey(organizationId));

			if (savedDockedState) {
				this.setState({
					...savedDockedState,
				});
			}
		}
	}

	private updateSpaceEditorFilterState = (
		filters: IFilterState = this.selectedView.filters,
		updateCaptions: boolean = true,
		addFullscreenLoaderText: boolean = true,
	) => {
		cancelAnimationFrame(this._requestAnimationFrameId);

		const ret = new Promise<void>((resolve) => {
			this._updateFilterStatePromiseResolveSet.add(resolve);
		});

		this._requestAnimationFrameId = window.requestAnimationFrame(() => {
			this.props.spaceViewRenderer.actions.updateSpaceEditorFilterState(filters, updateCaptions, addFullscreenLoaderText).then(() => {
				for (const resolve of [...this._updateFilterStatePromiseResolveSet]) {
					resolve();
					this._updateFilterStatePromiseResolveSet.delete(resolve);
				}
			});
		});

		return ret;
	};

	private onSpaceLoadReady = async () => {
		const {spaceViewRenderer} = this.props;
		this.props.appState.fullscreenLoaderText = "Applying layers, filters, captions...";
		await this.updateSpaceEditorFilterState(undefined, false, false);
		await spaceViewRenderer.actions.updateSpaceEditorCaptions(XyiconFeature.Boundary);
		this.props.appState.fullscreenLoaderText = "";
		this.startListening();
		this.forceUpdate();
	};

	public override async componentDidMount() {
		this.loadStateFromLocalStorage();
		window.addEventListener("hashchange", this.updateSpace);

		const {actions} = this.props.appState;
		const features = actions.getLoadingDependencies(XyiconFeature.SpaceEditor);

		for (const feature of features) {
			await this.props.transport.services.feature.refreshList(feature);
		}
		// Add potential new types
		actions.getSelectedView(XyiconFeature.SpaceEditor).updateSpaceEditorViewSettings();
		this.props.spaceViewRenderer.signals.spaceLoadReady.add(this.onSpaceLoadReady);
		await this.updateSpace();
		const {xyiconManager, boundaryManager, markupManager} = this.props.spaceViewRenderer;

		xyiconManager.signals.itemsAdd.add(this.forceUpdateArrow);
		boundaryManager.signals.itemsAdd.add(this.forceUpdateArrow);
		markupManager.signals.itemsAdd.add(this.forceUpdateArrow);

		xyiconManager.signals.itemsRemove.add(this.forceUpdateArrow);
		boundaryManager.signals.itemsRemove.add(this.forceUpdateArrow);
		markupManager.signals.itemsRemove.add(this.forceUpdateArrow);
	}

	public override async componentDidUpdate(prevProps: IBasicPropsFor3DTweaking) {
		await this.updateSpace();
		this.saveStateToLocalStorage();

		if (prevProps.appState !== this.props.appState) {
			this._appState = this.props.appState;
		}
	}

	public override componentWillUnmount() {
		this.stopListening();
		window.removeEventListener("hashchange", this.updateSpace);

		const {xyiconManager, boundaryManager, markupManager} = this.props.spaceViewRenderer;

		xyiconManager.signals.itemsAdd.remove(this.forceUpdateArrow);
		boundaryManager.signals.itemsAdd.remove(this.forceUpdateArrow);
		markupManager.signals.itemsAdd.remove(this.forceUpdateArrow);

		xyiconManager.signals.itemsRemove.remove(this.forceUpdateArrow);
		boundaryManager.signals.itemsRemove.remove(this.forceUpdateArrow);
		markupManager.signals.itemsRemove.remove(this.forceUpdateArrow);

		this.props.spaceViewRenderer.signals.spaceLoadReady.remove(this.onSpaceLoadReady);
	}

	private onDeleteCurrentUserFromViewShareList = () => {
		const {actions} = this.props.spaceViewRenderer;

		actions.selectViewById(actions.getViews(XyiconFeature.Space)[0].id);
	};

	public override render() {
		const space = this.props.appState.space;
		const {graphicalTools} = this.props.appState.app;
		const {photoSphereSceneManager} = graphicalTools;
		const dockedTitle = this.getDockedTitle();
		const isDockedAndOpen = !!dockedTitle;
		const isInMixedOrSplitViewMode = ["mixed", "split", "transitioning"].includes(photoSphereSceneManager.viewMode);

		if (space) {
			return (
				<div className="SpaceView">
					<Splitter
						ref={this.onSplitterDidMount}
						className={ReactUtils.cls({blurred: this.state.isCreatePanelOpen || this.state.sharingViewId})}
						onChange={this.onSplitterChange}
					>
						{!isInMixedOrSplitViewMode && isDockedAndOpen && (
							<Dockable
								spaceViewRenderer={this.props.spaceViewRenderer}
								setDocked={this.setDockableDocked}
								setOpen={this.setDockableOpen}
								isDocked={isDockedAndOpen}
								title={dockedTitle}
								setActiveTool={this.setActiveTool}
								onAddCatalogClick={this.onAddCatalogClick}
								onDuplicateCatalogClick={this.onDuplicateCatalogClick}
								onCreateUnplottedXyicons={Functions.emptyFunction}
							/>
						)}
						<SpaceEditor
							spaceViewRenderer={this.props.spaceViewRenderer}
							setDockableDocked={this.setDockableDocked}
							setDockableOpen={this.setDockableOpen}
							isCatalogOpen={this.state.isCatalogOpen}
							isBoundaryTypesWindowOpen={this.state.isBoundaryTypesWindowOpen}
							isUnplottedXyiconsOpen={this.state.isUnplottedXyiconsOpen}
							isCatalogDocked={this.state.isCatalogDocked}
							isBoundaryTypesWindowDocked={this.state.isBoundaryTypesWindowDocked}
							isUnplottedXyiconsDocked={this.state.isUnplottedXyiconsDocked}
							setScale={this.setScale}
							confirmAlignment={this.confirmAlignment}
							thumbnail={this.state.space?.thumbnailFileURL}
							mode={SpaceEditorMode.NORMAL}
							activeToolId={this.state.activeToolId}
							setActiveTool={this.setActiveTool}
							selectionToolType={this.state.selectionToolType}
							selectItems={this.selectItems}
							sidePanelRef={this._sidePanel}
							onToggleConditionalFormattingPanel={this.onToggleConditionalFormattingPanel}
							onToggleLayerPanel={this.onToggleLayerPanel}
							onToggleCaptionPanel={this.onToggleCaptionPanel}
							openOverlayerPanel={this.getOpenOverlayPanelType()}
							isCaptionPanelOpen={this.state.isCaptionPanelOpen}
							onAddCatalogClick={this.onAddCatalogClick}
							onDuplicateCatalogClick={this.onDuplicateCatalogClick}
							spaceViewShareClick={this.spaceViewShareClick}
							onOpenSpaceToPDFExportPanel={this.onOpenSpaceToPDFExportPanel}
						/>
						{!isInMixedOrSplitViewMode && this.getSidePanel()}
					</Splitter>
					{!isInMixedOrSplitViewMode && (
						<SpaceToPDFExportPanel
							isOpen={this.state.isSpaceToPDFExportPanelOpen}
							onClose={this.onCloseSpaceToPDFExportPanel}
						/>
					)}
					{!isInMixedOrSplitViewMode && (
						<>
							<div
								className={ReactUtils.cls("createPanel CatalogItemPanel SidePanel", {
									open: this.state.isCreatePanelOpen || this.state.managingColumns,
									manageColumns: this.state.managingColumns,
								})}
							>
								{this.state.isCreatePanelOpen && (
									<CatalogItemPanel
										onClose={this.closeCreatePanel}
										iconConfig={this.state.editedCatalog?.config}
										catalog={this.state.editedCatalog?.catalog}
										mode={this.state.catalogMode}
									/>
								)}
								{this.state.managingColumns && (
									<ManageColumnsPanel
										feature={this._sidePanel.current?.getSelectedGridTabFeature()}
										view={this.selectedView}
										onClose={this.onCloseManageColumns}
									/>
								)}
							</div>
							<div className={ReactUtils.cls("sharingPanel", {open: this.state.sharingViewId})}>
								<EditViewSharing
									view={this.props.appState.actions.getViewById(this.state.sharingViewId)}
									feature={XyiconFeature.SpaceEditor}
									close={() => this.toggleSharingPanel()}
									onDeleteCurrentUser={this.onDeleteCurrentUserFromViewShareList}
								/>
							</div>
						</>
					)}
				</div>
			);
		}

		// TODO loading?
		return null;
	}
}
